// ========================================
// BALANCE SHEET SERVICE
// Berechnet die Bilanz (Balance Sheet) mit allen Assets, Liabilities und Equity
// Berücksichtigt Expenses, Purchases, Sales, Cash Register, etc.
// ========================================

import { normalizeSale, normalizePurchase, normalizeExpense } from '../utils/dataNormalizer';
import { readActiveCurrencyType, toDisplayAmount } from '../utils/posCurrencyUtils';
import { calculateProfit } from '../domain/finance/profit';
import { calculateInventoryValue } from '../domain/inventory/valuation';
import { summarizeAccountsReceivable } from '../domain/customers/balance';
import {
  summarizeTotalAccountsPayable,
  summarizeUnpaidInvoicesDebt,
  summarizeOpenPayableInvoices,
  resolveInvoiceRemainingAmount
} from '../domain/suppliers/balance';
import { resolveSaleRegisterIncome } from '../domain/sales/checkout';
import {
  getOpeningCapitalAmount,
  mapFixedAssetsToBalanceSheet,
  getRemainingOpeningCapitalCash
} from '../utils/openingCapital';
import { isSetupPhase, resolveLiquidOutflowPortions } from '../utils/fundingSource';
import { collectUniqueSupplierPayments } from '../utils/supplierPaymentAggregation';
import DeviationService from './deviationService';

class BalanceSheetService {
  constructor() {
    this.languageContext = null;
  }

  /**
   * Setzt den Language Context
   */
  setLanguageContext(languageContext) {
    this.languageContext = languageContext;
  }

  /**
   * Übersetzung
   */
  t(key) {
    if (this.languageContext && this.languageContext.t) {
      return this.languageContext.t(key);
    }
    return key;
  }

  /**
   * Berechnet die Bilanz
   * @param {Object} data - Alle benötigten Daten
   * @returns {Object} Balance Sheet mit Assets, Liabilities, Equity
   */
  calculateBalanceSheet(data = {}) {
    const {
      sales = [],
      purchases = [],
      expenses = [],
      payments = [], // Payments from unified payment service
      cashRegister = null,
      electronicBalance = 0, // Electronic payments balance from payment report
      supplierDebts = [],
      customerDebts = [],
      inventoryValue = null, // null = compute from products/lots when present
      products = [],
      lots = [],
      bankAccounts = [],
      supermarketInvoices = [],
      discrepancyManagement = { selfDeviations: [], handoverDeviations: [] },
      expiredProducts = [],
      openingCapital = null,
      fixedAssets: fixedAssetsList = [],
      supermarketPayments = [],
      invoicePayments = [],
      stockConsumptions = [],
      stockSnapshots = null,
      /** When set, overrides install detection for opening-capital remapping */
      installationPhase: installationPhaseOverride = null
    } = data;
    
    // Debug logs removed — this path can run often on Reports re-renders;
    // noisy console.warn caused UI jank when bilan stayed unbalanced.
    
    // Filter expenses to avoid duplicates with payments
    // Expenses should be separate from purchase payments
    const operatingExpenses = expenses.filter(expense => {
      // Exclude expenses that are already tracked as purchase payments
      return !expense.purchaseId || expense.category !== 'purchase';
    });
    
    // Filter payments to get only expense payments (not purchase payments)
    const expensePayments = (payments || []).filter(payment => {
      return payment.transactionType === 'expense_payment' || 
             (payment.referenceId && expenses.some(e => e.id === payment.referenceId));
    });
    
    // Combine expenses and expense payments, avoiding duplicates
    const allExpenses = [...operatingExpenses];
    expensePayments.forEach(payment => {
      // Only add if not already in expenses
      if (!allExpenses.some(e => e.id === payment.referenceId)) {
        allExpenses.push({
          id: payment.id,
          date: payment.paymentDate,
          amount: payment.amount,
          category: 'other',
          paymentMethod: payment.paymentMethod,
          description: `Payment: ${payment.notes || ''}`
        });
      }
    });

    // ========================================
    // ASSETS (Vermögenswerte)
    // ========================================
    
    // ✅ WICHTIG: Inventory-Bewertung immer neu berechnen wenn products und lots vorhanden
    let inventory = 0;
    if (inventoryValue !== null && inventoryValue !== undefined) {
      // Verwende übergebenen Wert (z.B. aus Daily Closing)
      inventory = Number(inventoryValue) || 0;
    } else if (products && products.length > 0 && lots) {
      // ADR-0013: Inventory Valuation Owner (prefer stockSnapshots, lots fallback)
      try {
        const inventoryData = calculateInventoryValue({
          products,
          lots,
          stockSnapshots
        });
        inventory = inventoryData.totalValue || 0;
      } catch (error) {
        console.error('❌ [BALANCE SHEET] Error calculating inventory value:', error);
        inventory = 0;
      }
    } else {
      // Fallback: Verwende 0 wenn keine Daten vorhanden
      // Note: suppressed in production — this path is normal for new businesses
      inventory = 0;
    }
    
    // Current Assets (Umlaufvermögen)
    const liquidCash = this.calculateCash(cashRegister);
    // Capital d'ouverture (cash portion) belongs on the bilan, not on dashboard solde
    const setupOutflows = collectUniqueSupplierPayments({
      supermarketPayments,
      invoicePayments,
      supermarketInvoices
    });
    const financingOutflows = [...setupOutflows, ...(allExpenses || [])];
    // Same install detection as dashboard solde / liquid — needed so remaining OC cash
    // depletes for legacy register-tagged setup payments (start with balanceDifference 0).
    const installationPhase =
      installationPhaseOverride != null
        ? Boolean(installationPhaseOverride)
        : isSetupPhase({ sales });
    const openingCash = getRemainingOpeningCapitalCash({
      openingCapital,
      fixedAssets: fixedAssetsList,
      setupOutflows: financingOutflows,
      sales,
      installationPhase
    });
    const cash = liquidCash + openingCash;
    // Financement externe hors caisse = Fremdkapital (Passiva), sonst Bilan-Diff ≠ 0
    const externalFinancingTotal = financingOutflows.reduce((sum, entry) => {
      if (!entry) return sum;
      return (
        sum +
        (resolveLiquidOutflowPortions(entry, { installationPhase }).externalAmt || 0)
      );
    }, 0);
    // Bank balance = electronic balance from payment report + any bank accounts
    const bankFromAccounts = this.calculateBankBalance(bankAccounts);
    const bank = bankFromAccounts + (electronicBalance || 0); // Add electronic payments balance
    const accountsReceivable = this.calculateAccountsReceivable(customerDebts);
    const currentAssets = {
      cash,
      bank,
      accountsReceivable,
      inventory,
      prepaidExpenses: 0
    };

    // Fixed Assets (Anlagevermögen / Aktives Kapital) — Settings immobilisations
    const fixedAssets = mapFixedAssetsToBalanceSheet(fixedAssetsList);

    const totalFixedAssets = Object.values(fixedAssets).reduce((sum, val) => sum + (Number(val) || 0), 0);
    const totalCurrentAssets = Object.values(currentAssets).reduce((sum, val) => sum + (Number(val) || 0), 0);
    
    const totalAssets = {
      current: totalCurrentAssets,
      fixed: totalFixedAssets,
      activeCapital: totalFixedAssets, // Aktives Kapital = Anlagevermögen
      total: 0
    };
    totalAssets.total = totalCurrentAssets + totalFixedAssets;

    // ========================================
    // LIABILITIES (Verbindlichkeiten / Passiva)
    // ========================================
    
    // Current Liabilities — invoice ledger wins (no double-count with supplierDebts)
    // Rest = totalAmount − paidAmount; CON-Parent inkl. (gleiche Regel wie Payment/Dashboard)
    const accountsPayableTotal = summarizeTotalAccountsPayable({
      supplierDebts,
      supermarketInvoices
    });
    
    // Accepted écarts → P&L / equity (not Passiva). Pending/rejected ignored.
    const acceptedEcartImpact = this.calculateAcceptedEcartImpact(discrepancyManagement);
    
    // Ablaufsverluste = GuV/Eigenkapital (Inventar sinkt bereits), nie Fremdkapital.
    const expirationLosses = this.calculateExpirationLosses(expiredProducts, products, lots, purchases);
    
    // Passiv nach Quartal/Jahr berechnen
    const liabilitiesByPeriod = this.calculateLiabilitiesByPeriod(
      supplierDebts, 
      supermarketInvoices, 
      sales, 
      purchases, 
      expenses
    );
    
    const currentLiabilities = {
      accountsPayable: accountsPayableTotal,
      // Externe Finanzierungen (hors solde) — Gegenbuchung zu AP-Tilgung / Dépenses
      shortTermLoans: externalFinancingTotal,
      externalFinancing: externalFinancingTotal,
      accruedExpenses: 0,
      // Dépenses / Écarts / Ablauf → Gewinn/Eigenkapital, nicht Passiva
      quarterly: liabilitiesByPeriod.quarterly,
      yearly: liabilitiesByPeriod.yearly
    };

    // Long-term Liabilities (Langfristige Verbindlichkeiten)
    const longTermLiabilities = {
      loans: 0, // TODO: Später erweitern
      mortgages: 0 // TODO: Später erweitern
    };

    const totalCurrentLiabilities = accountsPayableTotal + externalFinancingTotal;
    
    const totalLongTermLiabilities = Object.values(longTermLiabilities).reduce((sum, val) => sum + (Number(val) || 0), 0);
    
    // Fremdkapital = Alle Verbindlichkeiten (kurz- und langfristig)
    const totalLiabilities = {
      current: totalCurrentLiabilities,
      longTerm: totalLongTermLiabilities,
      foreignCapital: totalCurrentLiabilities + totalLongTermLiabilities, // Fremdkapital
      total: 0
    };
    totalLiabilities.total = totalCurrentLiabilities + totalLongTermLiabilities;

    // ========================================
    // EQUITY (Eigenkapital)
    // ========================================
    
    const normalizedSales = (sales || []).map(normalizeSale);
    const normalizedPurchases = (purchases || []).map(normalizePurchase);
    const normalizedExpenses = allExpenses.map(normalizeExpense);

    // Gewinn inkl. Dépenses — ADR-0013 Finance Owner (FIFO + sale.inventoryCost + cuisine)
    const profitDataWithExpenses = calculateProfit({
      sales: normalizedSales,
      purchases: normalizedPurchases,
      expenses: normalizedExpenses,
      lots: lots || [],
      stockConsumptions: stockConsumptions || []
    });
    
    const totalRevenue = profitDataWithExpenses.revenue;
    const totalPurchaseCosts = profitDataWithExpenses.purchaseCosts;
    const totalOperatingExpenses = profitDataWithExpenses.operatingExpenses;
    const operatingNetProfit = profitDataWithExpenses.netProfit;
    // Accepted shortages + expiration write-offs reduce equity; surpluses increase it
    const netProfit =
      operatingNetProfit +
      acceptedEcartImpact.gains -
      acceptedEcartImpact.losses -
      expirationLosses;

    // Eigenkapital = Kapital + Gewinnrücklagen + Jahresgewinn
    const equity = {
      capital: getOpeningCapitalAmount(openingCapital),
      retainedEarnings: 0, // Gewinnrücklagen (kumulierte Gewinne aus Vorjahren)
      currentYearProfit: netProfit, // Jahresgewinn inkl. Écarts + Ablauf
      acceptedEcartLosses: acceptedEcartImpact.losses,
      acceptedEcartGains: acceptedEcartImpact.gains,
      expirationLosses,
      operatingProfitBeforeEcarts: operatingNetProfit
    };

    // Eigenkapital = Summe aller Equity-Komponenten
    const totalEquity = equity.capital + equity.retainedEarnings + equity.currentYearProfit;

    // ========================================
    // VALIDATION: Assets = Liabilities + Equity
    // ========================================
    // UI shows imbalance; do not console-spam on every Reports memo recompute
    const balanceCheck = totalAssets.total - (totalLiabilities.total + totalEquity);
    const isBalanced = Math.abs(balanceCheck) < 0.01; // Toleranz für Rundungsfehler

    return {
      date: new Date().toISOString(),
      assets: {
        current: currentAssets,
        fixed: fixedAssets,
        total: totalAssets
      },
      liabilities: {
        current: currentLiabilities,
        longTerm: longTermLiabilities,
        byPeriod: liabilitiesByPeriod, // Passiv nach Quartal/Jahr
        total: totalLiabilities,
        foreignCapital: totalLiabilities.foreignCapital // Fremdkapital
      },
      equity: {
        ...equity,
        total: totalEquity,
        ownCapital: totalEquity // Eigenkapital
      },
      // Zusammenfassung
      summary: {
        activeCapital: totalFixedAssets, // Aktives Kapital (Anlagevermögen)
        ownCapital: totalEquity, // Eigenkapital
        foreignCapital: totalLiabilities.foreignCapital // Fremdkapital
      },
      // Financial Summary
      financialSummary: {
        totalRevenue,
        totalPurchaseCosts,
        totalOperatingExpenses,
        netProfit,
        operatingProfitBeforeEcarts: operatingNetProfit,
        acceptedEcartLosses: acceptedEcartImpact.losses,
        acceptedEcartGains: acceptedEcartImpact.gains,
        profitMargin: totalRevenue > 0 ? (netProfit / totalRevenue) * 100 : 0,
        // Payment breakdown
        payments: {
          cash: this.calculateCashPayments(sales),
          electronic: this.calculateElectronicPayments(sales)
        }
      },
      // Validation
      isBalanced,
      balanceDifference: balanceCheck,
      // Metadata
      metadata: {
        salesCount: sales.length,
        purchasesCount: purchases.length,
        expensesCount: allExpenses.length,
        paymentsCount: (payments || []).length,
        supplierDebtsCount: supplierDebts.length,
        customerDebtsCount: customerDebts.length
      }
    };
  }

  /**
   * Berechnet Kassenbestand
   */
  calculateCash(cashRegister) {
    if (!cashRegister) return 0;
    
    // Wenn cashRegister ein Objekt mit balance ist
    if (typeof cashRegister === 'object' && cashRegister.balance !== undefined) {
      return Number(cashRegister.balance) || 0;
    }
    
    // Wenn cashRegister ein Array von Sessions ist
    if (Array.isArray(cashRegister)) {
      const activeSession = cashRegister.find(s => s.isActive);
      return activeSession ? (Number(activeSession.currentBalance) || 0) : 0;
    }
    
    return Number(cashRegister) || 0;
  }

  /**
   * Berechnet Bankguthaben
   */
  calculateBankBalance(bankAccounts) {
    if (!Array.isArray(bankAccounts)) return 0;
    return bankAccounts.reduce((sum, account) => sum + (Number(account.balance) || 0), 0);
  }

  /**
   * Créances clients = ventes à crédit (le client nous doit) — Aktiva.
   */
  calculateAccountsReceivable(customerDebts) {
    // ADR-0013: domain/customers/balance
    return summarizeAccountsReceivable(customerDebts);
  }

  /**
   * Berechnet unbezahlte Lieferanten-Rechnungen (AP) — ADR-0013 SSOT
   */
  calculateUnpaidInvoicesDebt(supermarketInvoices) {
    return summarizeUnpaidInvoicesDebt(supermarketInvoices);
  }

  /**
   * Berechnet Verbindlichkeiten nach Quartal und Jahr
   * Payable-Menge = dieselbe Liste wie Bilan AP / Payment-UI
   */
  calculateLiabilitiesByPeriod(supplierDebts, supermarketInvoices, sales, purchases, expenses) {
    const now = new Date();
    const currentQuarter = Math.floor(now.getMonth() / 3) + 1;
    const currentYear = now.getFullYear();
    
    // Berechne Quartalsende und Jahresende
    const quarterEnd = new Date(currentYear, currentQuarter * 3, 0);
    const yearEnd = new Date(currentYear, 11, 31);
    
    let quarterly = 0;
    let yearly = 0;
    const hasInvoiceLedger =
      Array.isArray(supermarketInvoices) && supermarketInvoices.length > 0;

    const accumulateByDue = (amount, dueDate) => {
      if (!(amount > 0)) return;
      if (dueDate) {
        if (dueDate <= quarterEnd) quarterly += amount;
        if (dueDate <= yearEnd) yearly += amount;
      } else {
        quarterly += amount;
        yearly += amount;
      }
    };

    const resolveInvoiceDueDate = (invoice) => {
      const raw =
        invoice?.dueDate ||
        invoice?.invoiceDate ||
        invoice?.date ||
        invoice?.createdAt ||
        null;
      if (!raw) return null;
      const parsed = new Date(raw);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    };

    // Invoice ledger wins — same payable set as summarizeTotalAccountsPayable
    if (hasInvoiceLedger) {
      const { invoices: payableInvoices } =
        summarizeOpenPayableInvoices(supermarketInvoices);
      payableInvoices.forEach((invoice) => {
        accumulateByDue(
          resolveInvoiceRemainingAmount(invoice),
          resolveInvoiceDueDate(invoice)
        );
      });
    } else if (Array.isArray(supplierDebts)) {
      supplierDebts.forEach((debt) => {
        const remainingDebt = Number(debt.remainingDebt) || 0;
        const dueDate = debt.dueDate ? new Date(debt.dueDate) : null;
        accumulateByDue(remainingDebt, dueDate);
      });
    }
    
    return {
      quarterly: Math.max(0, quarterly),
      yearly: Math.max(0, yearly)
    };
  }

  /**
   * Bar-Einnahmen aus Verkäufen (ADR-0013: resolveSaleRegisterIncome).
   * Credit/unpaid zählen nicht; Mixed nur Paid. Dépenses gehören nicht hierher.
   */
  calculateCashPayments(sales) {
    let total = 0;
    (sales || []).map(normalizeSale).forEach((sale) => {
      const { amount, channel } = resolveSaleRegisterIncome(sale);
      if (channel === 'cash') total += amount;
    });
    return total;
  }

  /**
   * Elektronische Einnahmen aus Verkäufen (ADR-0013: resolveSaleRegisterIncome).
   */
  calculateElectronicPayments(sales) {
    let total = 0;
    (sales || []).map(normalizeSale).forEach((sale) => {
      const { amount, channel } = resolveSaleRegisterIncome(sale);
      if (channel === 'electronic') total += amount;
    });
    return total;
  }

  /**
   * Gibt eine formatierte Bilanz zurück
   */
  getFormattedBalanceSheet(balanceSheet, language = 'fr') {
    const currency = readActiveCurrencyType();
    const locale = language === 'ar' ? 'ar-MR' : 'fr-MR';

    const formatCurrency = (amount) => {
      const displayAmount = toDisplayAmount(amount, currency);
      const formatted = new Intl.NumberFormat(locale, {
        style: 'decimal',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        numberingSystem: language === 'ar' ? 'latn' : undefined
      }).format(displayAmount);
      return `${formatted} ${currency}`;
    };

    return {
      ...balanceSheet,
      formatted: {
        totalAssets: formatCurrency(balanceSheet.assets.total.total),
        totalLiabilities: formatCurrency(balanceSheet.liabilities.total.total),
        totalEquity: formatCurrency(balanceSheet.equity.total),
        netProfit: formatCurrency(balanceSheet.financialSummary.netProfit),
        totalRevenue: formatCurrency(balanceSheet.financialSummary.totalRevenue),
        totalOperatingExpenses: formatCurrency(balanceSheet.financialSummary.totalOperatingExpenses)
      }
    };
  }

  /**
   * Berechnet jährliche Bilanz aus Tagesabschlüssen
   * @param {Array} dailyClosings - Array von Tagesabschlüssen
   * @param {Number} year - Jahr für die Bilanz (optional, default: aktuelles Jahr)
   * @returns {Object} Jahresbilanz
   */
  calculateYearlyBalanceSheetFromClosings(dailyClosings = [], year = null, extras = {}) {
    if (!Array.isArray(dailyClosings) || dailyClosings.length === 0) {
      console.warn('⚠️ [BALANCE SHEET] No daily closings provided for yearly calculation');
      return this.calculateBalanceSheet(extras);
    }

    const targetYear = year || new Date().getFullYear();

    // Filter closings for the target year
    const yearClosings = dailyClosings.filter(closing => {
      if (!closing.date) return false;
      const closingDate = new Date(closing.date);
      return closingDate.getFullYear() === targetYear;
    });

    if (yearClosings.length === 0) {
      console.warn(`⚠️ [BALANCE SHEET] No closings found for year ${targetYear}`);
      return this.calculateBalanceSheet(extras);
    }

    // Sort closings by date (oldest first)
    yearClosings.sort((a, b) => new Date(a.date) - new Date(b.date));

    // Get the last closing of the year (most recent)
    const lastClosing = yearClosings[yearClosings.length - 1];
    
    // Extract balance sheet from last closing if available
    let lastBalanceSheet = null;
    const extrasHaveCapital =
      Boolean(extras.openingCapital) ||
      (Array.isArray(extras.fixedAssets) && extras.fixedAssets.length > 0);
    if (lastClosing.balanceSheet) {
      lastBalanceSheet = lastClosing.balanceSheet;
      // Closings often omit capital — rebuild from summary + live settings when needed.
      const capitalMissing = !(Number(lastBalanceSheet?.equity?.capital) > 0);
      if (capitalMissing && extrasHaveCapital && lastClosing.closingSummary) {
        lastBalanceSheet = this.reconstructBalanceSheetFromClosingSummary(lastClosing, extras);
      }
    } else if (lastClosing.closingSummary) {
      lastBalanceSheet = this.reconstructBalanceSheetFromClosingSummary(lastClosing, extras);
    }

    // If we have a balance sheet from the last closing, use it
    if (lastBalanceSheet) {
      return {
        ...lastBalanceSheet,
        period: {
          type: 'yearly',
          year: targetYear,
          startDate: new Date(targetYear, 0, 1).toISOString(),
          endDate: new Date(targetYear, 11, 31).toISOString(),
          dailyClosingsCount: yearClosings.length
        },
        metadata: {
          ...lastBalanceSheet.metadata,
          year: targetYear,
          dailyClosingsCount: yearClosings.length,
          firstClosingDate: yearClosings[0]?.date,
          lastClosingDate: lastClosing.date
        }
      };
    }

    return this.calculateYearlyBalanceSheetFromAggregatedData(yearClosings, targetYear, extras);
  }

  /**
   * Rekonstruiert Bilanz aus Closing Summary
   * @param {object} [extras] — openingCapital / fixedAssets from live business settings
   */
  reconstructBalanceSheetFromClosingSummary(closing, extras = {}) {
    const summary = closing.closingSummary || {};
    
    return this.calculateBalanceSheet({
      sales: summary.sales || [],
      purchases: summary.purchases || [],
      expenses: summary.expenses || [],
      payments: summary.payments || [],
      cashRegister: summary.cashRegister || null,
      electronicBalance: summary.electronicBalance || 0,
      supplierDebts: summary.supplierDebts || [],
      customerDebts: summary.customerDebts || [],
      inventoryValue: summary.inventoryValue || 0,
      bankAccounts: summary.bankAccounts || [],
      supermarketInvoices: summary.supermarketInvoices || [],
      discrepancyManagement: summary.discrepancyManagement || extras.discrepancyManagement,
      expiredProducts: summary.expiredProducts || extras.expiredProducts || [],
      openingCapital: extras.openingCapital ?? summary.openingCapital ?? null,
      fixedAssets: extras.fixedAssets ?? summary.fixedAssets ?? [],
      supermarketPayments: summary.supermarketPayments || extras.supermarketPayments || [],
      invoicePayments: summary.invoicePayments || extras.invoicePayments || []
    });
  }

  /**
   * Berechnet Jahresbilanz aus aggregierten Daten aller Tagesabschlüsse
   */
  calculateYearlyBalanceSheetFromAggregatedData(yearClosings, year, extras = {}) {
    const aggregatedData = {
      sales: [],
      purchases: [],
      expenses: [],
      payments: [],
      supplierDebts: [],
      customerDebts: [],
      supermarketInvoices: [],
      cashRegister: null,
      electronicBalance: 0,
      inventoryValue: 0,
      bankAccounts: [],
      discrepancyManagement: { selfDeviations: [], handoverDeviations: [] },
      expiredProducts: []
    };

    // Collect all data from closings
    yearClosings.forEach(closing => {
      const summary = closing.closingSummary || {};
      
      if (summary.sales) aggregatedData.sales.push(...(Array.isArray(summary.sales) ? summary.sales : []));
      if (summary.purchases) aggregatedData.purchases.push(...(Array.isArray(summary.purchases) ? summary.purchases : []));
      if (summary.expenses) aggregatedData.expenses.push(...(Array.isArray(summary.expenses) ? summary.expenses : []));
      if (summary.payments) aggregatedData.payments.push(...(Array.isArray(summary.payments) ? summary.payments : []));
      if (summary.supplierDebts) aggregatedData.supplierDebts.push(...(Array.isArray(summary.supplierDebts) ? summary.supplierDebts : []));
      if (summary.customerDebts) aggregatedData.customerDebts.push(...(Array.isArray(summary.customerDebts) ? summary.customerDebts : []));
      if (summary.supermarketInvoices) aggregatedData.supermarketInvoices.push(...(Array.isArray(summary.supermarketInvoices) ? summary.supermarketInvoices : []));
      
      if (summary.discrepancyManagement) {
        const dm = summary.discrepancyManagement;
        if (dm.selfDeviations) aggregatedData.discrepancyManagement.selfDeviations.push(...(Array.isArray(dm.selfDeviations) ? dm.selfDeviations : []));
        if (dm.handoverDeviations) aggregatedData.discrepancyManagement.handoverDeviations.push(...(Array.isArray(dm.handoverDeviations) ? dm.handoverDeviations : []));
      }

      if (summary.expiredProducts) aggregatedData.expiredProducts.push(...(Array.isArray(summary.expiredProducts) ? summary.expiredProducts : []));

      // Use the last closing's values for these
      if (closing === yearClosings[yearClosings.length - 1]) {
        aggregatedData.cashRegister = summary.cashRegister || null;
        aggregatedData.electronicBalance = summary.electronicBalance || 0;
        aggregatedData.inventoryValue = summary.inventoryValue || 0;
        aggregatedData.bankAccounts = summary.bankAccounts || [];
      }
    });

    // Remove duplicates based on ID
    const uniqueSales = this.removeDuplicatesById(aggregatedData.sales, 'id');
    const uniquePurchases = this.removeDuplicatesById(aggregatedData.purchases, 'id');
    const uniqueExpenses = this.removeDuplicatesById(aggregatedData.expenses, 'id');
    const uniquePayments = this.removeDuplicatesById(aggregatedData.payments, 'id');
    const uniqueSupplierDebts = this.removeDuplicatesById(aggregatedData.supplierDebts, 'id');
    const uniqueCustomerDebts = this.removeDuplicatesById(aggregatedData.customerDebts, 'id');
    const uniqueSupermarketInvoices = this.removeDuplicatesById(aggregatedData.supermarketInvoices, 'id');

    const uniqueExpiredProducts = this.removeDuplicatesById(aggregatedData.expiredProducts, 'id');
    
    const balanceSheet = this.calculateBalanceSheet({
      sales: uniqueSales,
      purchases: uniquePurchases,
      expenses: uniqueExpenses,
      payments: uniquePayments,
      cashRegister: aggregatedData.cashRegister,
      electronicBalance: aggregatedData.electronicBalance,
      supplierDebts: uniqueSupplierDebts,
      customerDebts: uniqueCustomerDebts,
      inventoryValue: aggregatedData.inventoryValue,
      bankAccounts: aggregatedData.bankAccounts,
      supermarketInvoices: uniqueSupermarketInvoices,
      discrepancyManagement: aggregatedData.discrepancyManagement,
      expiredProducts: uniqueExpiredProducts,
      openingCapital: extras.openingCapital ?? null,
      fixedAssets: extras.fixedAssets ?? [],
      supermarketPayments: extras.supermarketPayments || [],
      invoicePayments: extras.invoicePayments || []
    });

    // Add period metadata
    return {
      ...balanceSheet,
      period: {
        type: 'yearly',
        year: year,
        startDate: new Date(year, 0, 1).toISOString(),
        endDate: new Date(year, 11, 31).toISOString(),
        dailyClosingsCount: yearClosings.length
      },
      metadata: {
        ...balanceSheet.metadata,
        year: year,
        dailyClosingsCount: yearClosings.length,
        firstClosingDate: yearClosings[0]?.date,
        lastClosingDate: yearClosings[yearClosings.length - 1]?.date
      }
    };
  }

  /**
   * Entfernt Duplikate aus Array basierend auf ID
   */
  removeDuplicatesById(array, idField = 'id') {
    if (!Array.isArray(array)) return [];
    
    const seen = new Set();
    return array.filter(item => {
      const id = item[idField];
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  }

  /**
   * Accepted manager écarts only → GuV / Eigenkapital.
   * Shortages = losses; surpluses = gains. Pending/rejected ignored.
   * @returns {{ losses: number, gains: number, net: number }}
   */
  calculateAcceptedEcartImpact(discrepancyManagement) {
    const empty = { losses: 0, gains: 0, net: 0 };
    if (!discrepancyManagement) return empty;

    const allDeviations = [
      ...(discrepancyManagement.selfDeviations || []),
      ...(discrepancyManagement.handoverDeviations || [])
    ];

    const accepted = allDeviations.filter((deviation) => {
      const status = deviation.status || deviation.managerReview?.decision || 'pending';
      return status === 'accepted';
    });

    let losses = 0;
    let gains = 0;
    for (const deviation of accepted) {
      const amount = DeviationService.signedDeviationAmount(deviation);
      if (amount < -0.01) losses += Math.abs(amount);
      else if (amount > 0.01) gains += amount;
    }

    return { losses, gains, net: gains - losses };
  }

  /**
   * Berechnet Verluste durch Ablauf (Expiration Losses)
   * @param {Array} expiredProducts - Array von abgelaufenen Produkten
   * @param {Array} products - Array von Produkten
   * @param {Array} lots - Array von Lots
   * @param {Array} purchases - Array von Purchases (Fallback)
   * @returns {number} Gesamter Verlust durch abgelaufene Produkte
   */
  calculateExpirationLosses(expiredProducts, products, lots, purchases) {
    if (!Array.isArray(expiredProducts) || expiredProducts.length === 0) return 0;
    
    const lotsArray = Array.isArray(lots) ? lots : Object.values(lots || {});
    
    // Berechne Verlust für jedes abgelaufene Produkt
    const totalLoss = expiredProducts.reduce((sum, expired) => {
      const quantity = Number(expired.quantity) || 0;
      if (quantity <= 0) return sum;
      
      let purchasePrice = 0;
      
      // Versuche zuerst, den Preis aus dem Lot zu bekommen
      if (expired.lotId && lotsArray.length > 0) {
        const lot = lotsArray.find(l => String(l.id) === String(expired.lotId));
        if (lot) {
          purchasePrice = Number(lot.purchaseUnitPrice) || 0;
        }
      }
      
      // Fallback: Verwende purchaseUnitPrice aus expired product
      if (purchasePrice === 0 && expired.purchaseUnitPrice) {
        purchasePrice = Number(expired.purchaseUnitPrice) || 0;
      }
      
      // Fallback: Suche in purchases
      if (purchasePrice === 0 && Array.isArray(purchases) && purchases.length > 0) {
        const purchase = purchases.find(p => String(p.productId) === String(expired.productId));
        if (purchase) {
          purchasePrice = Number(purchase.purchaseUnitPrice || purchase.purchasePrice || 0);
        }
      }
      
      // Fallback: Verwende Produkt-Preis
      if (purchasePrice === 0 && Array.isArray(products) && products.length > 0) {
        const product = products.find(p => String(p.id) === String(expired.productId));
        if (product) {
          purchasePrice = Number(product.purchaseUnitPrice || product.purchasePrice || 0);
        }
      }
      
      // Berechne Verlust: Menge * Einkaufspreis
      const loss = quantity * purchasePrice;
      return sum + loss;
    }, 0);
    
    return totalLoss;
  }
}

/** Totaux bilan — compatible structure imbriquée et anciens champs plats. */
export function resolveBalanceSheetTotals(balanceSheet) {
  if (!balanceSheet) return null;

  const assetsTotal =
    Number(balanceSheet.assets?.total?.total) ||
    Number(balanceSheet.totalAssets) ||
    0;
  const liabilitiesTotal =
    Number(balanceSheet.liabilities?.total?.total) ||
    Number(balanceSheet.liabilities?.total) ||
    Number(balanceSheet.totalLiabilities) ||
    0;
  const equityTotal =
    Number(balanceSheet.equity?.total ?? balanceSheet.summary?.ownCapital) ||
    Number(balanceSheet.totalEquity) ||
    0;

  return {
    assetsTotal,
    liabilitiesTotal,
    equityTotal,
    capital: Number(balanceSheet.equity?.capital) || 0,
    retainedEarnings: Number(balanceSheet.equity?.retainedEarnings) || 0,
    currentYearProfit: Number(balanceSheet.equity?.currentYearProfit) || 0,
    foreignCapital:
      Number(balanceSheet.liabilities?.foreignCapital ?? balanceSheet.summary?.foreignCapital) ||
      0,
    accountsPayable: Number(balanceSheet.liabilities?.current?.accountsPayable) || 0,
    isBalanced: Boolean(balanceSheet.isBalanced),
    balanceDifference: Number(balanceSheet.balanceDifference) || 0
  };
}

// Singleton instance
const balanceSheetService = new BalanceSheetService();
export default balanceSheetService;















